<template>
  <div class="accordion" :class="accordionClass"><a class="accordion__header" href="#" :class="headerClass" @click.prevent="activate">
      <icon class="accordion-icon" :name="currentIcon" size="9px"></icon><span class="accordion__menu-title">
        <slot name="heading">{{ heading }}</slot></span></a>
    <transition :name="transition">
      <div class="accordion__content" v-if="active" ref="content" :style="contentStyle">
        <slot></slot>
      </div>
    </transition>
  </div>
</template>

<script>
import Icon from '@/scripts/components/basic/Icon.vue'

export default {
  name: 'CollectionAccordionB',
  components: { Icon },
  props: {
    expandMaxHeight: {
      type: Number
    },
    heading: {
      type: String
    },
    selected: {
      type: Boolean,
      default: false
    },
    activeIcon: {
      type: String,
      default: 'chevron-up'
    },
    inactiveIcon: {
      type: String,
      default: 'chevron-down'
    },
    iconSize: {
      type: String,
      default: '9px'
    },
    /** prevent the accordion from closing when another is opened in the same accordion group */
    autoClose: {
      type: Boolean,
      default: true
    },
    theme: {
      type: String,
      default: 'default',
      validator (value) {
        return [
          'default',
          'product-details',
          'faq'
        ].includes(value)
      }
    }
  },
  data () {
    return {
      active: true,
      contentStyle: {},
      scrollHeight: 0,
      transition: 'none'
    }
  },
  computed: {
    accordionClass () {
      const classes = []

      if (this.active) {
        classes.push('is-open')
      }

      return classes
    },
    headerClass () {
      return {
        'is-open': this.active
      }
    },
    currentIcon () {
      return (this.active) ? this.activeIcon : this.inactiveIcon
    }
  },
  mounted () {
    this.setContentHeight()

    // NOTE: this has to be set to false after mount or scrollHeight will be 0
    this.active = this.selected
    this.transition = 'slide-vertical'

    if (this.$parent.addAccordion) {
      this.$parent.addAccordion(this)
    }
  },
  beforeDestroy () {
    this.active = false
    if (this.$parent.removeAccordion) {
      this.$parent.removeAccordion(this)
    }
  },
  methods: {
    setContentHeight () {
      const height = this.$refs.content.scrollHeight
      const containerPadding = 50

      // Set max height for animation
      this.contentStyle = {
        'max-height': `${this.expandMaxHeight || (height + containerPadding)}px`
      }
    },
    activate () {
      if (this.$parent.activate && !this.active) {
        this.$parent.activate(this)
        this.$emit('active')
      } else {
        this.active = !this.active
      }

      if (this.active) { this.$nextTick(() => this.setContentHeight()) }
    },
    addMaxHeight () {
      this.$nextTick(() => this.setContentHeight())
    }
  }
}
</script>

<style scoped lang="scss">
  .accordion {
    &.accordion--product-details {
      border-top: 1px solid $color-grey;
      background-color: transparent;

      &:last-child {
        border-bottom: 1px solid $color-grey;
      }
    }

    // &.accordion {
    //   border-bottom: 1px solid $color-light-grey;
    // }

    &.accordion--faq {
      margin-bottom: 0;
      border-top: 1px solid $color-light-grey;
      background-color: transparent;
      transition: background-color 300ms ease-in-out;

      &:last-child {
        border-bottom: 1px solid $color-light-grey;
      }

      &.is-open {
        border-top-color: $color-grey;
        background-color: $color-light-grey;
      }

      &.is-open:last-child {
        border-bottom-color: $color-grey;
      }

      &.is-open + .accordion--faq {
        border-top-color: $color-grey;
      }
    }

    &__header {
      width: 100%;
      padding: 18px 16px 18px 0;
      display: flex;
      justify-content: flex-start;
      align-items: center;
      position: relative;

      .accordion & {
        padding-right: 0;
        padding-left: 0;
      }
    }

    &__menu-title {
      font-family: $font-family-primary;
      font-size: 15px;
      color: $color-black-900;

      .accordion--faq & {
        @include h5();

        line-height: 1.29;
        letter-spacing: 0;

        @include tablet-up {
          font-size: rem(16);
          line-height: 1.5;
          letter-spacing: 1px;
        }
      }

      .accordion & {
        margin-right: 8px;
        font-weight: 500;
        line-height: 18px;
        @include desktop-up {
          line-height: 22px;
        }
      }
    }

    &__content {
      overflow: hidden;

      &--wrapper {
        overflow: hidden;
      }
    }

    &__header {

      .footer--center .accordion & {
        @include mobile-only {
          justify-content: center;
        }
      }

      .accordion--product-details & {
        font-weight: 700;
      }

      .accordion--faq & {
        padding: 12px 8px;
        background-color: transparent;
        transition: background-color 300ms ease-in-out;

        @include tablet-up {
          padding: 16px 12px;
        }
      }

      .accordion--faq &:hover {
        background-color: $color-light-grey;
      }
    }

    &__content {

      .accordion--product-details & {
        padding: 0 16px 24px;
      }

      .accordion--faq & {
        margin-bottom: 0;
        padding: 16px 10px 16px 14px;
        line-height: 1.43;
        border-top: 1px solid $color-light-grey;

        @include tablet-up {
          padding: 24px 24px;
        }
      }

      .accordion--faq.is-open & {
        border-top-color: $color-grey;
      }
    }

  }

  .footer-main-color-light .accordion__menu-title {
    color: $color-white;
  }

  .collection-mobile-menu__contents .accordion.sort-mobile-menu .accordion__menu-title {
    @include h8;
  }

  .footer-main-color-light .accordion__menu-title {

    &:hover {

      &::before {
        background-color: $color-white;
      }
    }

    &::before {
      background-color: $color-white;
    }
  }

  .footer-main-color-dark .accordion__menu-title {
    color: $color-black;
  }

  .footer-main-color-dark .accordion__menu-title {

    &:hover {

      &::before {
        background-color: $color-black;
      }
    }

    &::before {
      background-color: $color-black;
    }
  }

  .accordion-icon {
    flex-shrink: 0;
    margin-right: 8px;
    order: 1;
    margin-left: auto;

    .footer-main-color-light & {
      fill: $color-white;
    }

    .footer-main-color-dark & {
      fill: $color-black;
    }
  }

  .sort-mobile-menu {
    border-bottom: 1px solid $color-light-grey;
    /deep/ .accordion {
      &__menu-title {
        font-family: $font-family-secondary;
        font-size: 14px;
        color: $color-black-900;
        letter-spacing: 0;

        @include desktop-up {
          font-family: $font-family-primary;
        }
      }
    }
  }

</style>
